import { type AthenaRightKey, parseAthenaRightKey } from "../../rights/key.ts";
import { missingRequiredRights } from "../../rights/matching.ts";
import type { AthenaPrincipal } from "../../runtime/data/principal.ts";
import { AthenaBillingAuthorizationError } from "../errors.ts";
import type {
	BillingOperation,
	BillingOperationCapability,
} from "./capabilities.ts";
import type { BillingInvocationAuthority } from "./invocation-authority.ts";
import {
	isProcessBillingInvocation,
	SESSION_BILLING_INVOCATION,
} from "./invocation-authority.ts";

const CATALOG_READ = parseAthenaRightKey("billing.catalog.read");
const PAYMENTS_READ = parseAthenaRightKey("billing.payments.read");
const PAYMENTS_WRITE = parseAthenaRightKey("billing.payments.write");
const CUSTOMERS_READ = parseAthenaRightKey("billing.customers.read");
const CUSTOMERS_WRITE = parseAthenaRightKey("billing.customers.write");
const REFUNDS_READ = parseAthenaRightKey("billing.refunds.read");
const REFUNDS_WRITE = parseAthenaRightKey("billing.refunds.write");
const SUBSCRIPTIONS_READ = parseAthenaRightKey("billing.subscriptions.read");
const SUBSCRIPTIONS_WRITE = parseAthenaRightKey("billing.subscriptions.write");
const PAYMENT_LINKS_READ = parseAthenaRightKey("billing.payment-links.read");
const PAYMENT_LINKS_WRITE = parseAthenaRightKey("billing.payment-links.write");
const SALES_INVOICES_READ = parseAthenaRightKey("billing.sales-invoices.read");
const WEBHOOKS_READ = parseAthenaRightKey("billing.webhooks.read");
const WEBHOOKS_WRITE = parseAthenaRightKey("billing.webhooks.write");
const SELF_INVOICES_READ = parseAthenaRightKey("billing.self.invoices.read");
const SELF_PAYMENTS_READ = parseAthenaRightKey("billing.self.payments.read");
const SELF_SUBSCRIPTION_READ = parseAthenaRightKey(
	"billing.self.subscription.read",
);
const SELF_SUBSCRIPTION_WRITE = parseAthenaRightKey(
	"billing.self.subscription.write",
);
const SELF_CHECKOUT_WRITE = parseAthenaRightKey("billing.self.checkout.write");
const ADMIN_RECONCILIATION_WRITE = parseAthenaRightKey(
	"billing.admin.reconciliation.write",
);
const ADMIN_WEBHOOKS_READ = parseAthenaRightKey("billing.admin.webhooks.read");
const ADMIN_WEBHOOKS_WRITE = parseAthenaRightKey("billing.admin.webhooks.write");
const ADMIN_INGESTION_READ = parseAthenaRightKey("billing.admin.ingestion.read");
const ADMIN_CONFLICTS_WRITE = parseAthenaRightKey(
	"billing.admin.conflicts.write",
);

/** Process-owned overlay Rights — dialect execute keys, not grant-catalog. */
export const BILLING_DIALECT_RIGHTS: readonly AthenaRightKey[] = Object.freeze([
	CATALOG_READ,
	PAYMENTS_READ,
	PAYMENTS_WRITE,
	CUSTOMERS_READ,
	CUSTOMERS_WRITE,
	REFUNDS_READ,
	REFUNDS_WRITE,
	SUBSCRIPTIONS_READ,
	SUBSCRIPTIONS_WRITE,
	PAYMENT_LINKS_READ,
	PAYMENT_LINKS_WRITE,
	SALES_INVOICES_READ,
	WEBHOOKS_READ,
	WEBHOOKS_WRITE,
	SELF_INVOICES_READ,
	SELF_PAYMENTS_READ,
	SELF_SUBSCRIPTION_READ,
	SELF_SUBSCRIPTION_WRITE,
	SELF_CHECKOUT_WRITE,
	ADMIN_RECONCILIATION_WRITE,
	ADMIN_WEBHOOKS_READ,
	ADMIN_WEBHOOKS_WRITE,
	ADMIN_INGESTION_READ,
	ADMIN_CONFLICTS_WRITE,
]);

/** In-process dialect Rights bag. Not an HTTP identity and not detected by userId. */
export const PROCESS_OWNED_BILLING_PRINCIPAL: AthenaPrincipal = {
	authenticated: true,
	grants: [],
	rights: BILLING_DIALECT_RIGHTS,
	userId: "athena.billing.process",
};

/**
 * Catalog reads do not require a provider-subject binding. Everything else
 * is operator-only until an Athena billing subject is bound.
 */
const BILLING_UNBOUND_SESSION_OPERATIONS = new Set<BillingOperation>([
	"prices.list",
	"products.list",
	"self.invoices.list",
	"self.invoices.get",
	"self.payments.list",
	"self.payments.get",
	"self.subscription.get",
	"self.subscription.cancel",
	"self.subscription.enroll",
	"self.checkout.create",
]);

export function isProcessOwnedBillingPrincipal(
	principal: AthenaPrincipal,
): boolean {
	return principal === PROCESS_OWNED_BILLING_PRINCIPAL;
}

export const BILLING_OPERATION_RIGHTS: Record<
	BillingOperation,
	readonly AthenaRightKey[]
> = {
	"customers.create": [CUSTOMERS_WRITE],
	"customers.delete": [CUSTOMERS_WRITE],
	"customers.get": [CUSTOMERS_READ],
	"customers.list": [CUSTOMERS_READ],
	"customers.update": [CUSTOMERS_WRITE],
	"invoices.get": [SALES_INVOICES_READ],
	"invoices.list": [SALES_INVOICES_READ],
	"paymentLinks.create": [PAYMENT_LINKS_WRITE],
	"paymentLinks.delete": [PAYMENT_LINKS_WRITE],
	"paymentLinks.get": [PAYMENT_LINKS_READ],
	"paymentLinks.list": [PAYMENT_LINKS_READ],
	"paymentLinks.update": [PAYMENT_LINKS_WRITE],
	"payments.cancel": [PAYMENTS_WRITE],
	"payments.create": [PAYMENTS_WRITE],
	"payments.get": [PAYMENTS_READ],
	"payments.list": [PAYMENTS_READ],
	"refunds.cancel": [REFUNDS_WRITE],
	"refunds.create": [REFUNDS_WRITE],
	"refunds.get": [REFUNDS_READ],
	"refunds.list": [REFUNDS_READ],
	"subscriptions.cancel": [SUBSCRIPTIONS_WRITE],
	"subscriptions.create": [SUBSCRIPTIONS_WRITE],
	"subscriptions.get": [SUBSCRIPTIONS_READ],
	"subscriptions.list": [SUBSCRIPTIONS_READ],
	"subscriptions.update": [SUBSCRIPTIONS_WRITE],
	"webhooks.create": [WEBHOOKS_WRITE],
	"webhooks.delete": [WEBHOOKS_WRITE],
	"webhooks.get": [WEBHOOKS_READ],
	"webhooks.list": [WEBHOOKS_READ],
	"webhooks.test": [WEBHOOKS_WRITE],
	"webhooks.update": [WEBHOOKS_WRITE],
	"products.list": [CATALOG_READ],
	"prices.list": [CATALOG_READ],
	"checkout.create": [PAYMENTS_WRITE],
	"self.invoices.list": [SELF_INVOICES_READ],
	"self.invoices.get": [SELF_INVOICES_READ],
	"self.payments.list": [SELF_PAYMENTS_READ],
	"self.payments.get": [SELF_PAYMENTS_READ],
	"self.subscription.get": [SELF_SUBSCRIPTION_READ],
	"self.subscription.cancel": [SELF_SUBSCRIPTION_WRITE],
	"self.subscription.enroll": [SELF_SUBSCRIPTION_WRITE],
	"self.checkout.create": [SELF_CHECKOUT_WRITE],
	"admin.connections.materialize": [ADMIN_RECONCILIATION_WRITE],
	"admin.conflicts.resolve": [ADMIN_CONFLICTS_WRITE],
	"admin.ingestion.health": [ADMIN_INGESTION_READ],
	"admin.reconciliation.retry": [ADMIN_RECONCILIATION_WRITE],
	"admin.reconciliation.run": [ADMIN_RECONCILIATION_WRITE],
	"admin.webhooks.reconcile": [ADMIN_WEBHOOKS_WRITE],
	"admin.webhooks.status": [ADMIN_WEBHOOKS_READ],
	"admin.webhooks.verify": [ADMIN_WEBHOOKS_WRITE],
};

export function requiredBillingRights(
	operation: BillingOperation,
): readonly AthenaRightKey[] {
	return BILLING_OPERATION_RIGHTS[operation];
}

/**
 * Rights gate for local Billing. Grants are never consulted.
 * Returns a deny error, or undefined when the principal holds the Rights.
 */
export function authorizeBillingOperation(
	principal: AthenaPrincipal,
	operation: BillingOperation,
): AthenaBillingAuthorizationError | undefined {
	const required = requiredBillingRights(operation);
	const missing = missingRequiredRights(principal.rights, required);
	if (missing.length === 0) {
		return undefined;
	}
	return new AthenaBillingAuthorizationError({
		missing,
		operation,
	});
}

/**
 * Session principals cannot run merchant-wide Billing. Process invocation
 * authority is the operator path — never a forgeable principal userId.
 */
export function authorizeBillingSubjectBinding(
	principal: AthenaPrincipal,
	operation: BillingOperation | string,
	authority: BillingInvocationAuthority = SESSION_BILLING_INVOCATION,
): AthenaBillingAuthorizationError | undefined {
	void principal;
	if (isProcessBillingInvocation(authority)) {
		return undefined;
	}
	if (
		typeof operation === "string" &&
		BILLING_UNBOUND_SESSION_OPERATIONS.has(operation as BillingOperation)
	) {
		return undefined;
	}
	if (typeof operation === "string" && operation.startsWith("self.")) {
		return undefined;
	}
	return new AthenaBillingAuthorizationError({
		missing: [],
		operation,
	});
}

export function denyBillingInvocation(
	principal: AthenaPrincipal,
	operation: BillingOperation,
	authority: BillingInvocationAuthority = SESSION_BILLING_INVOCATION,
): AthenaBillingAuthorizationError | undefined {
	if (isProcessBillingInvocation(authority)) {
		return authorizeBillingOperation(principal, operation);
	}
	return (
		authorizeBillingOperation(principal, operation) ??
		authorizeBillingSubjectBinding(principal, operation, authority)
	);
}

export function callerEffectiveBillingCapability(input: {
	authority?: BillingInvocationAuthority;
	capability: BillingOperationCapability;
	operation: BillingOperation;
	principal: AthenaPrincipal;
}): BillingOperationCapability {
	if (input.capability.available !== true) {
		return input.capability;
	}
	const authority = input.authority ?? SESSION_BILLING_INVOCATION;
	const denied = denyBillingInvocation(
		input.principal,
		input.operation,
		authority,
	);
	if (denied == null) {
		return input.capability;
	}
	const sessionMerchant =
		authority.kind === "session" &&
		!BILLING_UNBOUND_SESSION_OPERATIONS.has(input.operation);
	return {
		available: false,
		reason: sessionMerchant ? "operator_only" : "missing_permission",
	};
}
